import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function AddCook() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId } = useBranchFilter();
  
  // Add Cook form state
  const [cookForm, setCookForm] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    phone: '',
    address: '',
    country: 'Kenya',
    city: '',
    postalCode: '',
    qualification: '', // Cooking certifications
    experience: '',
    salary: '',
    joiningDate: '',
    emergencyContact: '',
    emergencyPhone: '',
    nationalId: '',
    employeeId: '',
    profilePicture: null
  });
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Country code states
  const [cookPhoneCountryCode, setCookPhoneCountryCode] = useState('+254'); // Default: Kenya
  const [cookEmergencyPhoneCountryCode, setCookEmergencyPhoneCountryCode] = useState('+254'); // Default: Kenya
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  // Add Cook form functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCookForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Generate password function: word + numbers (minimum 6 characters total)
  const generatePassword = () => {
    const words = ['book', 'star', 'moon', 'tree', 'bird', 'fish', 'rock', 'wind', 'fire', 'wave', 'lake', 'hill', 'rose', 'leaf', 'snow', 'rain', 'sun', 'sky', 'sea', 'gem'];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    const wordLength = randomWord.length;
    
    // Calculate how many digits we need to ensure minimum 6 characters total
    // If word is 3 chars, need 3 digits (total 6)
    // If word is 4 chars, need 2 digits (total 6)
    // If word is 5+ chars, need 1 digit (total 6+)
    const minDigitsNeeded = Math.max(1, 6 - wordLength);
    
    // Generate random number with appropriate number of digits
    // For 1 digit: 0-9
    // For 2 digits: 10-99
    // For 3 digits: 100-999
    const maxNumber = Math.pow(10, minDigitsNeeded) - 1;
    const minNumber = minDigitsNeeded === 1 ? 0 : Math.pow(10, minDigitsNeeded - 1);
    const randomNumber = Math.floor(Math.random() * (maxNumber - minNumber + 1)) + minNumber;
    const randomDigits = randomNumber.toString().padStart(minDigitsNeeded, '0');
    
    return randomWord + randomDigits;
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setCookForm(prev => ({
      ...prev,
      profilePicture: file
    }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeProfilePicture = () => {
    setCookForm(prev => ({
      ...prev,
      profilePicture: null
    }));
    setProfilePicturePreview(null);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!cookForm.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!cookForm.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!cookForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(cookForm.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!cookForm.password.trim()) {
      errors.password = 'Password is required';
    } else if (cookForm.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (!cookForm.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (cookForm.password !== cookForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (!cookForm.dateOfBirth.trim()) {
      errors.dateOfBirth = 'Date of birth is required';
    }
    
    if (!cookForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (!cookForm.address.trim()) {
      errors.address = 'Address is required';
    }
    
    if (!cookForm.country.trim()) {
      errors.country = 'Country is required';
    }
    
    if (!cookForm.city.trim()) {
      errors.city = 'City/Town is required';
    }
    
    if (!cookForm.postalCode.trim()) {
      errors.postalCode = 'Postal Code is required';
    }
    
    if (!cookForm.qualification.trim()) {
      errors.qualification = 'Cooking certification/qualification is required';
    }
    
    if (!cookForm.experience.trim()) {
      errors.experience = 'Experience is required';
    } else if (isNaN(cookForm.experience) || parseInt(cookForm.experience) < 0) {
      errors.experience = 'Experience must be a valid number';
    }
    
    if (!cookForm.salary.trim()) {
      errors.salary = 'Salary is required';
    } else if (isNaN(cookForm.salary) || parseFloat(cookForm.salary) <= 0) {
      errors.salary = 'Salary must be a valid positive number';
    }
    
    if (!cookForm.joiningDate.trim()) {
      errors.joiningDate = 'Joining date is required';
    }
    
    if (!cookForm.emergencyContact.trim()) {
      errors.emergencyContact = 'Emergency contact is required';
    }
    
    if (!cookForm.emergencyPhone.trim()) {
      errors.emergencyPhone = 'Emergency phone is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // First create a Supabase Auth user
      console.log('Creating Supabase auth user for cook:', cookForm.email);
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: cookForm.email,
        password: cookForm.password,
        options: {
          data: {
            first_name: cookForm.firstName,
            last_name: cookForm.lastName,
            user_type: 'cook',
            school_id: schoolData.id
          }
        }
      });
      
      if (authError) {
        console.error('Auth error:', authError);
        // Email strictness removed - if email already exists in auth, continue without blocking
        if (authError.message && authError.message.includes('already registered')) {
          console.log('Email already registered in auth.users (may be used for another user type), continuing...');
          // Continue without blocking - the cook record will be created without auth_user_id
        } else {
          alert(`Failed to create cook account: ${authError.message}`);
          setIsSubmitting(false);
          return;
        }
      }

      if (!authData.user) {
        console.error('No user data returned from auth signup');
        alert('Failed to create cook account. Please try again.');
        setIsSubmitting(false);
        return;
      }

      console.log('Auth user created successfully:', authData.user.id);

      // Create user record in users table
      try {
        console.log('Creating user record in users table');
        const { data: userRecord, error: userError } = await supabase
          .from('users')
          .insert({
            id: authData.user.id,
            email: cookForm.email,
            first_name: cookForm.firstName,
            last_name: cookForm.lastName,
            user_type: 'cook'
          })
          .select()
          .single();
        
        if (userError) {
          console.warn('Could not create cook user record:', userError);
          // Continue anyway - auth user exists
        } else {
          console.log('Cook user record created successfully:', userRecord);
        }
      } catch (userErr) {
        console.warn('Cook user record creation failed:', userErr);
        // Continue anyway - auth user exists
      }
      
      // Upload profile picture if provided
      let profilePictureUrl = null;
      if (cookForm.profilePicture) {
        try {
          setUploadingPicture(true);
          const fileExt = cookForm.profilePicture.name.split('.').pop();
          const fileName = `cook_${authData.user.id}_${Date.now()}.${fileExt}`;
          const filePath = `cook-profiles/${schoolData.id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('teacher-uploads')
            .upload(filePath, cookForm.profilePicture, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Error uploading profile picture:', uploadError);
            // Continue without profile picture if upload fails
          } else {
            // Get public URL
            const { data: urlData } = supabase.storage
              .from('teacher-uploads')
              .getPublicUrl(filePath);
            profilePictureUrl = urlData.publicUrl;
          }
        } catch (uploadErr) {
          console.error('Error uploading profile picture:', uploadErr);
          // Continue without profile picture if upload fails
        } finally {
          setUploadingPicture(false);
        }
      }
      
      // Create cook data
      const cookData = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null, // Include branch_id if a branch is selected
        auth_user_id: authData.user.id,
        first_name: cookForm.firstName.trim(),
        middle_name: cookForm.middleName.trim() || null,
        last_name: cookForm.lastName.trim(),
        email: cookForm.email.trim(),
        date_of_birth: cookForm.dateOfBirth,
        phone: cookForm.phone.trim() ? `${cookPhoneCountryCode} ${cookForm.phone.trim()}` : '',
        address: cookForm.address.trim(),
        country: cookForm.country.trim(),
        city: cookForm.city.trim(),
        postal_code: cookForm.postalCode.trim(),
        qualification: cookForm.qualification.trim(),
        experience: parseInt(cookForm.experience),
        salary: parseFloat(cookForm.salary),
        joining_date: cookForm.joiningDate,
        emergency_contact: cookForm.emergencyContact.trim(),
        emergency_phone: cookForm.emergencyPhone.trim() ? `${cookEmergencyPhoneCountryCode} ${cookForm.emergencyPhone.trim()}` : '',
        national_id: cookForm.nationalId.trim() || null,
        employee_id: cookForm.employeeId.trim() || null,
        profile_picture_url: profilePictureUrl,
        is_active: true,
        created_at: new Date().toISOString()
      };
      
      console.log('Saving cook data to Supabase:', cookData);
      
      const { data, error } = await supabase
        .from('cooks')
        .insert(cookData)
        .select()
        .single();
      
      if (error) {
        console.error('Error saving cook:', error);
        // Email strictness removed - allow same email for different user types
        // Show generic error message
        alert(`Failed to add cook: ${error.message}`);
        setIsSubmitting(false);
        return;
      }
      
      console.log('Cook added successfully:', data);
      console.log('Cook can now login with email:', cookForm.email, 'and password');
      
      // Reset form
      setCookForm({
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        dateOfBirth: '',
        phone: '',
        address: '',
        country: 'Kenya',
        city: '',
        postalCode: '',
        qualification: '',
        experience: '',
        salary: '',
        joiningDate: '',
        emergencyContact: '',
        emergencyPhone: '',
        nationalId: '',
        employeeId: '',
        profilePicture: null
      });
      setProfilePicturePreview(null);
      setFormErrors({});
      
      // Show success popup
      setSuccessData({
        email: cookForm.email,
        password: cookForm.password,
        name: `${cookForm.firstName} ${cookForm.lastName}`,
        employeeId: cookForm.employeeId || 'N/A'
      });
      setShowSuccessPopup(true);
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to add cook. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <h1>Add New Cook</h1>
          <div className="user-info">
            <span>{schoolData?.school_name || 'School'}</span>
            <div className="user-avatar">🏫</div>
          </div>
        </div>
        
        <div className="content-body">
          <div className="dashboard-content">
            <div className="add-teacher-section">
              <div className="add-teacher-card">
                <form onSubmit={handleSubmit} className="add-teacher-form">
                  <div className="form-section">
                    <h3>Cook Information</h3>
                    <div className="form-row three-columns">
                      <div className="form-group">
                        <label htmlFor="firstName">First Name *</label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={cookForm.firstName}
                          onChange={handleInputChange}
                          className={formErrors.firstName ? 'error' : ''}
                          placeholder="Enter first name"
                        />
                        {formErrors.firstName && (
                          <span className="error-text">{formErrors.firstName}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="middleName">Middle Name</label>
                        <input
                          type="text"
                          id="middleName"
                          name="middleName"
                          value={cookForm.middleName}
                          onChange={handleInputChange}
                          placeholder="Enter middle name (optional)"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="lastName">Last Name *</label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={cookForm.lastName}
                          onChange={handleInputChange}
                          className={formErrors.lastName ? 'error' : ''}
                          placeholder="Enter last name"
                        />
                        {formErrors.lastName && (
                          <span className="error-text">{formErrors.lastName}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>
                      <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                          Profile Picture
                        </label>
                        <div style={{
                          position: 'relative',
                          marginTop: '0.5rem',
                          width: '100%'
                        }}>
                          <input
                            type="file"
                            id="profilePicture"
                            name="profilePicture"
                            accept="image/*"
                            onChange={handleProfilePictureChange}
                            style={{
                              position: 'absolute',
                              width: '0.1px',
                              height: '0.1px',
                              opacity: 0,
                              overflow: 'hidden',
                              zIndex: -1,
                              pointerEvents: 'none'
                            }}
                          />
                          <label
                            htmlFor="profilePicture"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '0.4rem 0.6rem',
                              background: '#FFFFFF',
                              color: '#374151',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: '400',
                              transition: 'all 0.2s ease',
                              width: '100%',
                              textAlign: 'center',
                              gap: '0.5rem',
                              userSelect: 'none',
                              boxSizing: 'border-box'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#F9FAFB';
                              e.currentTarget.style.borderColor = '#9CA3AF';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#FFFFFF';
                              e.currentTarget.style.borderColor = '#D1D5DB';
                            }}
                          >
                            <span style={{ fontSize: '0.9rem' }}>📷</span>
                            <span>{cookForm.profilePicture ? 'Change' : 'Choose File'}</span>
                          </label>
                        </div>
                        {(cookForm.profilePicture || profilePicturePreview) && (
                          <div style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            background: '#F9FAFB',
                            borderRadius: '8px',
                            border: '1px solid #E5E7EB',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}>
                            <div style={{
                              width: '80px',
                              height: '80px',
                              borderRadius: '50%',
                              overflow: 'hidden',
                              border: '3px solid #FF8C42',
                              flexShrink: 0
                            }}>
                              {profilePicturePreview ? (
                                <img 
                                  src={profilePicturePreview} 
                                  alt="Profile preview" 
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <div style={{
                                  width: '100%',
                                  height: '100%',
                                  background: '#E5E7EB',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '2rem'
                                }}>
                                  👤
                                </div>
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              {cookForm.profilePicture && (
                                <>
                                  <div style={{ 
                                    fontWeight: '600', 
                                    color: '#1F2937', 
                                    marginBottom: '0.25rem', 
                                    wordBreak: 'break-word',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}>
                                    {cookForm.profilePicture.name}
                                  </div>
                                  <div style={{ fontSize: '0.85rem', color: '#6B7280', whiteSpace: 'nowrap' }}>
                                    {(cookForm.profilePicture.size / 1024 / 1024).toFixed(2)} MB
                                  </div>
                                </>
                              )}
                            </div>
                            {cookForm.profilePicture && (
                              <button
                                type="button"
                                onClick={removeProfilePicture}
                                style={{
                                  padding: '0.4rem 0.6rem',
                                  background: '#EF4444',
                                  color: '#FFFFFF',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  fontWeight: '500',
                                  transition: 'all 0.2s ease',
                                  flexShrink: 0,
                                  whiteSpace: 'nowrap',
                                  width: 'auto',
                                  height: 'auto',
                                  lineHeight: '1.2'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = '#DC2626';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = '#EF4444';
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        )}
                        <small style={{ 
                          display: 'block', 
                          marginTop: '0.5rem', 
                          color: '#6B7280', 
                          fontSize: '0.85rem',
                          fontStyle: 'italic'
                        }}>
                          Max size: 5MB. Accepted formats: JPG, PNG, GIF
                        </small>
                      </div>
                      
                      <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <label htmlFor="nationalId" style={{ marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                          National/Government ID
                        </label>
                        <input
                          type="text"
                          id="nationalId"
                          name="nationalId"
                          value={cookForm.nationalId}
                          onChange={handleInputChange}
                          placeholder="Enter National/Government ID (optional)"
                          style={{
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="employeeId">Employee ID</label>
                        <input
                          type="text"
                          id="employeeId"
                          name="employeeId"
                          value={cookForm.employeeId}
                          onChange={handleInputChange}
                          placeholder="Enter employee ID (optional)"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={cookForm.email}
                          onChange={handleInputChange}
                          className={formErrors.email ? 'error' : ''}
                          placeholder="cook@example.com"
                        />
                        {formErrors.email && (
                          <span className="error-text">{formErrors.email}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="dateOfBirth">Date of Birth *</label>
                        <input
                          type="date"
                          id="dateOfBirth"
                          name="dateOfBirth"
                          value={cookForm.dateOfBirth}
                          onChange={handleInputChange}
                          className={formErrors.dateOfBirth ? 'error' : ''}
                        />
                        {formErrors.dateOfBirth && (
                          <span className="error-text">{formErrors.dateOfBirth}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="phone">Phone *</label>
                        <div className="phone-input-wrapper">
                          <select
                            className="country-code-select"
                            value={cookPhoneCountryCode}
                            onChange={(e) => setCookPhoneCountryCode(e.target.value)}
                          >
                            <option value="+1">🇺🇸 +1 (US/CA)</option>
                            <option value="+44">🇬🇧 +44 (UK)</option>
                            <option value="+91">🇮🇳 +91 (India)</option>
                            <option value="+92">🇵🇰 +92 (Pakistan)</option>
                            <option value="+971">🇦🇪 +971 (UAE)</option>
                            <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                            <option value="+974">🇶🇦 +974 (Qatar)</option>
                            <option value="+965">🇰🇼 +965 (Kuwait)</option>
                            <option value="+973">🇧🇭 +973 (Bahrain)</option>
                            <option value="+968">🇴🇲 +968 (Oman)</option>
                            <option value="+961">🇱🇧 +961 (Lebanon)</option>
                            <option value="+962">🇯🇴 +962 (Jordan)</option>
                            <option value="+20">🇪🇬 +20 (Egypt)</option>
                            <option value="+27">🇿🇦 +27 (South Africa)</option>
                            <option value="+61">🇦🇺 +61 (Australia)</option>
                            <option value="+64">🇳🇿 +64 (New Zealand)</option>
                            <option value="+86">🇨🇳 +86 (China)</option>
                            <option value="+81">🇯🇵 +81 (Japan)</option>
                            <option value="+82">🇰🇷 +82 (South Korea)</option>
                            <option value="+65">🇸🇬 +65 (Singapore)</option>
                            <option value="+60">🇲🇾 +60 (Malaysia)</option>
                            <option value="+66">🇹🇭 +66 (Thailand)</option>
                            <option value="+62">🇮🇩 +62 (Indonesia)</option>
                            <option value="+63">🇵🇭 +63 (Philippines)</option>
                            <option value="+84">🇻🇳 +84 (Vietnam)</option>
                            <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                            <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                            <option value="+977">🇳🇵 +977 (Nepal)</option>
                            <option value="+33">🇫🇷 +33 (France)</option>
                            <option value="+49">🇩🇪 +49 (Germany)</option>
                            <option value="+39">🇮🇹 +39 (Italy)</option>
                            <option value="+34">🇪🇸 +34 (Spain)</option>
                            <option value="+31">🇳🇱 +31 (Netherlands)</option>
                            <option value="+32">🇧🇪 +32 (Belgium)</option>
                            <option value="+41">🇨🇭 +41 (Switzerland)</option>
                            <option value="+43">🇦🇹 +43 (Austria)</option>
                            <option value="+46">🇸🇪 +46 (Sweden)</option>
                            <option value="+47">🇳🇴 +47 (Norway)</option>
                            <option value="+45">🇩🇰 +45 (Denmark)</option>
                            <option value="+358">🇫🇮 +358 (Finland)</option>
                            <option value="+7">🇷🇺 +7 (Russia)</option>
                            <option value="+90">🇹🇷 +90 (Turkey)</option>
                            <option value="+52">🇲🇽 +52 (Mexico)</option>
                            <option value="+55">🇧🇷 +55 (Brazil)</option>
                            <option value="+54">🇦🇷 +54 (Argentina)</option>
                            <option value="+57">🇨🇴 +57 (Colombia)</option>
                            <option value="+234">🇳🇬 +234 (Nigeria)</option>
                            <option value="+254">🇰🇪 +254 (Kenya)</option>
                            <option value="+212">🇲🇦 +212 (Morocco)</option>
                            <option value="+972">🇮🇱 +972 (Israel)</option>
                            <option value="+98">🇮🇷 +98 (Iran)</option>
                            <option value="+964">🇮🇶 +964 (Iraq)</option>
                            <option value="+967">🇾🇪 +967 (Yemen)</option>
                            <option value="+963">🇸🇾 +963 (Syria)</option>
                          </select>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={cookForm.phone}
                            onChange={handleInputChange}
                            className={formErrors.phone ? 'error' : ''}
                            placeholder="e.g. 300 1234567"
                          />
                        </div>
                        {formErrors.phone && (
                          <span className="error-text">{formErrors.phone}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="address">Address *</label>
                        <input
                          type="text"
                          id="address"
                          name="address"
                          value={cookForm.address}
                          onChange={handleInputChange}
                          className={formErrors.address ? 'error' : ''}
                          placeholder="Enter street address"
                        />
                        {formErrors.address && (
                          <span className="error-text">{formErrors.address}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row three-columns">
                      <div className="form-group">
                        <label htmlFor="country">Country *</label>
                        <input
                          type="text"
                          id="country"
                          name="country"
                          value={cookForm.country}
                          onChange={handleInputChange}
                          className={formErrors.country ? 'error' : ''}
                          placeholder="Enter country"
                        />
                        {formErrors.country && (
                          <span className="error-text">{formErrors.country}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="city">City / Town *</label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={cookForm.city}
                          onChange={handleInputChange}
                          className={formErrors.city ? 'error' : ''}
                          placeholder="Enter city or town"
                        />
                        {formErrors.city && (
                          <span className="error-text">{formErrors.city}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="postalCode">Postal Code *</label>
                        <input
                          type="text"
                          id="postalCode"
                          name="postalCode"
                          value={cookForm.postalCode}
                          onChange={handleInputChange}
                          className={formErrors.postalCode ? 'error' : ''}
                          placeholder="Enter postal code"
                        />
                        {formErrors.postalCode && (
                          <span className="error-text">{formErrors.postalCode}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Professional Information</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="qualification">Cooking Certification/Qualification *</label>
                        <input
                          type="text"
                          id="qualification"
                          name="qualification"
                          value={cookForm.qualification}
                          onChange={handleInputChange}
                          className={formErrors.qualification ? 'error' : ''}
                          placeholder="e.g., Food Safety Certificate, Culinary Diploma"
                        />
                        {formErrors.qualification && (
                          <span className="error-text">{formErrors.qualification}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="experience">Experience (Years) *</label>
                        <input
                          type="number"
                          id="experience"
                          name="experience"
                          value={cookForm.experience}
                          onChange={handleInputChange}
                          className={formErrors.experience ? 'error' : ''}
                          placeholder="e.g., 5"
                          min="0"
                        />
                        {formErrors.experience && (
                          <span className="error-text">{formErrors.experience}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="salary">Salary *</label>
                        <input
                          type="number"
                          id="salary"
                          name="salary"
                          value={cookForm.salary}
                          onChange={handleInputChange}
                          className={formErrors.salary ? 'error' : ''}
                          placeholder="e.g., 50000"
                          min="0"
                          step="0.01"
                        />
                        {formErrors.salary && (
                          <span className="error-text">{formErrors.salary}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="joiningDate">Joining Date *</label>
                        <input
                          type="date"
                          id="joiningDate"
                          name="joiningDate"
                          value={cookForm.joiningDate}
                          onChange={handleInputChange}
                          className={formErrors.joiningDate ? 'error' : ''}
                        />
                        {formErrors.joiningDate && (
                          <span className="error-text">{formErrors.joiningDate}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Cook Credentials</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <div className="password-input-wrapper" style={{ position: 'relative' }}>
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={cookForm.password}
                            onChange={handleInputChange}
                            className={formErrors.password ? 'error' : ''}
                            placeholder="Enter password"
                            style={{ paddingRight: '2.5rem' }}
                          />
                          <span 
                            className="password-toggle-icon"
                            onClick={() => setShowPassword(!showPassword)}
                            title={showPassword ? "Hide password" : "Show password"}
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              userSelect: 'none',
                              zIndex: 1
                            }}
                          >
                            {showPassword ? '🙈' : '👁️'}
                          </span>
                        </div>
                        {formErrors.password && (
                          <span className="error-text">{formErrors.password}</span>
                        )}
                        <div style={{ marginTop: '0.5rem' }}>
                          <button
                            type="button"
                            className="toggle-btn"
                            onClick={() => {
                              const genPassword = generatePassword();
                              setCookForm(prev => ({
                                ...prev,
                                password: genPassword,
                                confirmPassword: genPassword
                              }));
                            }}
                            style={{
                              padding: '0.4rem 0.8rem',
                              backgroundColor: '#10B981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              fontSize: '0.85rem'
                            }}
                          >
                            Generate Password (min 6 characters)
                          </button>
                          <small style={{ display: 'block', marginTop: '0.25rem', color: '#6B7280', fontSize: '0.75rem' }}>
                            Remember: Password must be at least 6 characters
                          </small>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password *</label>
                        <div className="password-input-wrapper" style={{ position: 'relative' }}>
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            id="confirmPassword"
                            name="confirmPassword"
                            value={cookForm.confirmPassword}
                            onChange={handleInputChange}
                            className={formErrors.confirmPassword ? 'error' : ''}
                            placeholder="Confirm password"
                            style={{ paddingRight: '2.5rem' }}
                          />
                          <span 
                            className="password-toggle-icon"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            title={showConfirmPassword ? "Hide password" : "Show password"}
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              userSelect: 'none',
                              zIndex: 1
                            }}
                          >
                            {showConfirmPassword ? '🙈' : '👁️'}
                          </span>
                        </div>
                        {formErrors.confirmPassword && (
                          <span className="error-text">{formErrors.confirmPassword}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Emergency Contact</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="emergencyContact">Emergency Contact *</label>
                        <input
                          type="text"
                          id="emergencyContact"
                          name="emergencyContact"
                          value={cookForm.emergencyContact}
                          onChange={handleInputChange}
                          className={formErrors.emergencyContact ? 'error' : ''}
                          placeholder="Emergency contact name"
                        />
                        {formErrors.emergencyContact && (
                          <span className="error-text">{formErrors.emergencyContact}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="emergencyPhone">Emergency Phone *</label>
                        <div className="phone-input-wrapper">
                          <select
                            className="country-code-select"
                            value={cookEmergencyPhoneCountryCode}
                            onChange={(e) => setCookEmergencyPhoneCountryCode(e.target.value)}
                          >
                            <option value="+1">🇺🇸 +1 (US/CA)</option>
                            <option value="+44">🇬🇧 +44 (UK)</option>
                            <option value="+91">🇮🇳 +91 (India)</option>
                            <option value="+92">🇵🇰 +92 (Pakistan)</option>
                            <option value="+971">🇦🇪 +971 (UAE)</option>
                            <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                            <option value="+974">🇶🇦 +974 (Qatar)</option>
                            <option value="+965">🇰🇼 +965 (Kuwait)</option>
                            <option value="+973">🇧🇭 +973 (Bahrain)</option>
                            <option value="+968">🇴🇲 +968 (Oman)</option>
                            <option value="+961">🇱🇧 +961 (Lebanon)</option>
                            <option value="+962">🇯🇴 +962 (Jordan)</option>
                            <option value="+20">🇪🇬 +20 (Egypt)</option>
                            <option value="+27">🇿🇦 +27 (South Africa)</option>
                            <option value="+61">🇦🇺 +61 (Australia)</option>
                            <option value="+64">🇳🇿 +64 (New Zealand)</option>
                            <option value="+86">🇨🇳 +86 (China)</option>
                            <option value="+81">🇯🇵 +81 (Japan)</option>
                            <option value="+82">🇰🇷 +82 (South Korea)</option>
                            <option value="+65">🇸🇬 +65 (Singapore)</option>
                            <option value="+60">🇲🇾 +60 (Malaysia)</option>
                            <option value="+66">🇹🇭 +66 (Thailand)</option>
                            <option value="+62">🇮🇩 +62 (Indonesia)</option>
                            <option value="+63">🇵🇭 +63 (Philippines)</option>
                            <option value="+84">🇻🇳 +84 (Vietnam)</option>
                            <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                            <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                            <option value="+977">🇳🇵 +977 (Nepal)</option>
                            <option value="+33">🇫🇷 +33 (France)</option>
                            <option value="+49">🇩🇪 +49 (Germany)</option>
                            <option value="+39">🇮🇹 +39 (Italy)</option>
                            <option value="+34">🇪🇸 +34 (Spain)</option>
                            <option value="+31">🇳🇱 +31 (Netherlands)</option>
                            <option value="+32">🇧🇪 +32 (Belgium)</option>
                            <option value="+41">🇨🇭 +41 (Switzerland)</option>
                            <option value="+43">🇦🇹 +43 (Austria)</option>
                            <option value="+46">🇸🇪 +46 (Sweden)</option>
                            <option value="+47">🇳🇴 +47 (Norway)</option>
                            <option value="+45">🇩🇰 +45 (Denmark)</option>
                            <option value="+358">🇫🇮 +358 (Finland)</option>
                            <option value="+7">🇷🇺 +7 (Russia)</option>
                            <option value="+90">🇹🇷 +90 (Turkey)</option>
                            <option value="+52">🇲🇽 +52 (Mexico)</option>
                            <option value="+55">🇧🇷 +55 (Brazil)</option>
                            <option value="+54">🇦🇷 +54 (Argentina)</option>
                            <option value="+57">🇨🇴 +57 (Colombia)</option>
                            <option value="+234">🇳🇬 +234 (Nigeria)</option>
                            <option value="+254">🇰🇪 +254 (Kenya)</option>
                            <option value="+212">🇲🇦 +212 (Morocco)</option>
                            <option value="+972">🇮🇱 +972 (Israel)</option>
                            <option value="+98">🇮🇷 +98 (Iran)</option>
                            <option value="+964">🇮🇶 +964 (Iraq)</option>
                            <option value="+967">🇾🇪 +967 (Yemen)</option>
                            <option value="+963">🇸🇾 +963 (Syria)</option>
                          </select>
                          <input
                            type="tel"
                            id="emergencyPhone"
                            name="emergencyPhone"
                            value={cookForm.emergencyPhone}
                            onChange={handleInputChange}
                            className={formErrors.emergencyPhone ? 'error' : ''}
                            placeholder="e.g. 300 1234567"
                          />
                        </div>
                        {formErrors.emergencyPhone && (
                          <span className="error-text">{formErrors.emergencyPhone}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={() => navigate('/school/dashboard')}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="submit-btn"
                      disabled={isSubmitting || uploadingPicture}
                    >
                      {uploadingPicture ? 'Uploading Picture...' : isSubmitting ? 'Adding Cook...' : 'Add Cook'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && successData && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowSuccessPopup(false);
            setSuccessData(null);
          }}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              animation: 'slideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3)'
            }}>
              <span style={{ fontSize: '3rem', color: '#FFFFFF' }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              textAlign: 'center',
              color: '#1F2937',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '0.5rem'
            }}>
              Cook Added Successfully!
            </h2>

            {/* Subtitle */}
            <p style={{
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.95rem',
              marginBottom: '2rem'
            }}>
              {successData.name} has been added to the system
            </p>

            {/* Credentials Card */}
            <div style={{
              background: '#F9FAFB',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              border: '2px solid #E5E7EB'
            }}>
              <h3 style={{
                color: '#374151',
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                Login Credentials
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Employee ID */}
                {successData.employeeId && successData.employeeId !== 'N/A' && (
                  <div style={{
                    background: '#FFFFFF',
                    borderRadius: '8px',
                    padding: '1rem',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      fontWeight: '500',
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Employee ID
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      color: '#1F2937',
                      fontWeight: '600',
                      wordBreak: 'break-all'
                    }}>
                      {successData.employeeId}
                    </div>
                  </div>
                )}

                {/* Email */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Email
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: '#1F2937',
                    fontWeight: '600',
                    wordBreak: 'break-all'
                  }}>
                    {successData.email}
                  </div>
                </div>

                {/* Password */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Password
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: '#1F2937',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    letterSpacing: '1px'
                  }}>
                    {successData.password}
                  </div>
                </div>
              </div>
            </div>

            {/* Info Message */}
            <div style={{
              background: '#EFF6FF',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              border: '1px solid #BFDBFE'
            }}>
              <p style={{
                color: '#1E40AF',
                fontSize: '0.875rem',
                margin: 0,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}>
                <span>ℹ️</span>
                <span>Please share these credentials with the cook</span>
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowSuccessPopup(false);
                setSuccessData(null);
              }}
              style={{
                width: '100%',
                padding: '0.875rem 1.5rem',
                background: '#FF8C42',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#FFA366';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(255, 140, 66, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#FF8C42';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddCook;

